import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  ActionList,
  ActionListItem,
  Amount,
  Avatar,
  Badge,
  Box,
  Button,
  CheckCircleIcon,
  XCircleIcon,
  Divider,
  DownloadIcon,
  Dropdown,
  DropdownOverlay,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectInput,
  Text,
  TextInput,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { useHistory } from 'react-router-dom';
import { LOAN_STATUS_COLOR, LOAN_STATUS } from 'components/LoansAndAdvances/constants';
import { routePaths } from 'components/Routes/data';
import { RejectApplicationModal } from './RejectApplicationModal';
import { BasicUserLoanData, EditApproveModalProps, LoanFormState, SalaryImpactRow } from './types';
import { LoanCalculatorResponseSchemaContract } from 'schemas/LoanCalculatorResponseSchema';
import {
  INTEREST_TYPES,
  BASIC_USER_LOAN_DATA_KEY,
  ACTIVE_LOANS_BY_USER_KEY,
  LOAN_CALCULATE_KEY,
} from './constants';
import { formatMonthYear, computeEligibilityChecks, formatTenure } from './utils';
import { ACTION_MODAL_Z_INDEX } from 'components/SalaryStructures/creation/components/styles';

export const EditApproveModal = ({ isOpen, onClose, loan, onSuccess }: EditApproveModalProps) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [form, setForm] = useState<LoanFormState>({
    amount: '',
    interestType: '0',
    interestRate: '',
    perquisiteRate: '',
    tenure: '',
    emiStartsFrom: '',
  });

  useEffect(() => {
    if (loan) {
      setForm({
        amount: String(loan.principal ?? ''),
        interestType: String(loan.interest_type ?? ''),
        interestRate: loan.interest_rate != null ? String(loan.interest_rate) : '',
        perquisiteRate: loan.perk_rate != null ? String(loan.perk_rate) : '',
        tenure: String(loan.tenure ?? ''),
        emiStartsFrom: loan.recovery_start_from ?? '',
      });
    }
  }, [loan]);

  const isInterestFree = form.interestType === '4';

  const isApproveRejectDisabled =
    !isInterestFree &&
    (form.interestRate === '' || form.perquisiteRate === '' || Number(form.perquisiteRate) < 0);

  const onEditAndApproveSuccess = (successMsg: string) => {
    queryClient.invalidateQueries(['LOANS']);
    setIsRejectDialogOpen(false);
    onClose();
    onSuccess?.(successMsg);
  };

  const editAndApproveMutation = useMutation({
    mutationFn: async () => {
      const activeLoan = loan!;
      await api.loans.editPendingLoanTerms(activeLoan.id, {
        principal: Number(form.amount),
        tenure: Number(form.tenure),
        recovery_start_from: form.emiStartsFrom,
        interest_type: Number(form.interestType),
        interest_rate: Number(form.interestRate || 0),
        perk_rate: Number(form.perquisiteRate || 0),
        loan_category_id: activeLoan.loan_category_id ?? 0,
      });
      return api.loans.updateLoanStatus(activeLoan.id, { status: 'loan-approved', remarks: null });
    },
    onSuccess: () => onEditAndApproveSuccess('Request successfully approved.'),
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to approve request. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const downloadDocumentMutation = useMutation({
    mutationFn: (fileId: string) => api.loans.downloadLoanDocument(fileId),
    onSuccess: (res) => {
      window.open(res.signed_url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to fetch document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const startDateOptions = React.useMemo(() => {
    const base = new Date();
    base.setDate(1);
    return Array.from({ length: 3 }, (_, i) => {
      const d = new Date(base);
      d.setMonth(d.getMonth() + i);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      return { label: formatMonthYear(value), value };
    });
  }, []);

  const { data: basicUserData } = useQuery<BasicUserLoanData>({
    queryKey: [BASIC_USER_LOAN_DATA_KEY, loan?.people_id, form.emiStartsFrom],
    queryFn: () => api.loans.basicUserLoanData(loan!.people_id, form.emiStartsFrom || undefined),
    enabled: !!loan?.people_id && isOpen,
  });

  const { data: activeLoansData } = useQuery({
    queryKey: [ACTIVE_LOANS_BY_USER_KEY, loan?.people_id],
    queryFn: () =>
      api.loans.fetchLoanData({ user_id: loan!.people_id, status: LOAN_STATUS.ACTIVE }),
    enabled: !!loan?.people_id && isOpen,
  });
  const activeLoans = activeLoansData?.data ?? [];

  const apiInterestType = Number(form.interestType);
  const calculatedPrincipal = Number(form.amount);
  const calculatedTenure = Number(form.tenure);
  const calculatedInterestRate = isInterestFree ? 0 : Number(form.interestRate);
  const calculatedPerkRate = isInterestFree ? 0 : Number(form.perquisiteRate);

  const isCalculateEnabled =
    isOpen &&
    !!loan &&
    calculatedPrincipal > 0 &&
    calculatedTenure > 0 &&
    (isInterestFree || calculatedInterestRate > 0);

  const {
    data: calculatedLoan,
    refetch: calculateLoan,
    isFetching: isCalculating,
  } = useQuery<LoanCalculatorResponseSchemaContract>({
    queryKey: [
      LOAN_CALCULATE_KEY,
      calculatedPrincipal,
      calculatedTenure,
      apiInterestType,
      calculatedInterestRate,
      calculatedPerkRate,
    ],
    queryFn: () =>
      api.loans.calculateLoan({
        principal: calculatedPrincipal,
        interest_rate: calculatedInterestRate,
        tenure: calculatedTenure,
        interest_type: apiInterestType,
        perk_rate: calculatedPerkRate,
      }),
    enabled: false,
    keepPreviousData: true,
  });

  const eligibilityChecks = computeEligibilityChecks(basicUserData, loan?.monthly_total_emi ?? 0);
  const eligibilityPassedCount = eligibilityChecks.filter((c) => c.passed).length;

  if (!loan) return null;

  const monthlySalary =
    basicUserData?.salary != null ? Math.round(basicUserData.salary / 12) : null;
  const newMonthlyEmi = calculatedLoan?.monthly_total_emi ?? loan.monthly_total_emi;
  const estNewTakeHome =
    monthlySalary != null && newMonthlyEmi != null ? monthlySalary - newMonthlyEmi : null;

  const salaryImpactRows: SalaryImpactRow[] = [
    { label: 'Current Monthly Salary', value: monthlySalary },
    { label: 'Existing EMIs', value: basicUserData?.total_recovery_for_month ?? null },
    { label: 'New Monthly EMI', value: newMonthlyEmi },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={onClose} size="full" zIndex={ACTION_MODAL_Z_INDEX}>
        <ModalHeader
          title="Employee Request"
          titleSuffix={
            <Badge color={LOAN_STATUS_COLOR[loan.status] ?? 'neutral'}>#{loan.id}</Badge>
          }
        />
        <ModalBody height="100%">
          <Box display="flex" gap="spacing.6">
            <Box flex={1} display="flex" flexDirection="column" gap="spacing.5" width="100%">
              <Box
                borderWidth="thin"
                borderRadius="medium"
                borderColor="surface.border.gray.subtle"
                backgroundColor="surface.background.gray.moderate"
                padding="spacing.7">
                <Box display="flex" alignItems="center" gap="spacing.4" marginBottom="spacing.5">
                  <Avatar name={loan.name ?? 'Employee'} size="medium" color="positive" />
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Box display="flex" alignItems="center" gap="spacing.3">
                      <Text size="medium" variant="body" weight="semibold">
                        {loan.name ?? '—'}
                      </Text>
                      <Badge color="positive" size="small">
                        Active Employee
                      </Badge>
                    </Box>
                    <Text
                      size="small"
                      variant="body"
                      weight="regular"
                      color="surface.text.gray.muted">
                      {loan.employee_id ?? '—'}
                    </Text>
                  </Box>
                </Box>
                <Divider />
                <Box display="flex" marginY="spacing.5">
                  <Divider orientation="vertical" />
                  <Box flex={1} paddingX="spacing.5">
                    <Text
                      size="small"
                      weight="medium"
                      variant="body"
                      color="surface.text.gray.muted">
                      Tenure
                    </Text>
                    <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                      {formatTenure(basicUserData?.hire_date)}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box flex={1} paddingX="spacing.5">
                    <Text
                      size="small"
                      weight="medium"
                      variant="body"
                      color="surface.text.gray.muted">
                      Annual Salary
                    </Text>
                    <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                      {basicUserData?.salary != null ? (
                        <Amount value={basicUserData.salary} size="small" />
                      ) : (
                        '—'
                      )}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box flex={1} paddingLeft="spacing.5">
                    <Text
                      size="small"
                      weight="medium"
                      variant="body"
                      color="surface.text.gray.muted">
                      Ongoing Loans
                    </Text>
                    {activeLoans.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap="spacing.1">
                        {activeLoans.map((activeLoan, index) => (
                          <React.Fragment key={activeLoan.id}>
                            <Link
                              size="small"
                              onClick={() => {
                                onClose();
                                history.push(
                                  `${routePaths.loansV2.root}?loan_id=${activeLoan.id}&status=${LOAN_STATUS.ACTIVE}`,
                                );
                              }}>
                              #{activeLoan.id}
                            </Link>
                            {index < activeLoans.length - 1 && <Text size="small">,</Text>}
                          </React.Fragment>
                        ))}
                      </Box>
                    ) : (
                      <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                        —
                      </Text>
                    )}
                  </Box>
                </Box>

                <Box display="flex" marginTop="spacing.7">
                  <Divider orientation="vertical" />
                  <Box flex={1} paddingX="spacing.5">
                    <Text
                      size="small"
                      weight="medium"
                      variant="body"
                      color="surface.text.gray.muted">
                      Reason
                    </Text>
                    <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                      {loan.loan_category_name ?? '—'}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box flex={1} paddingX="spacing.5">
                    <Text
                      size="small"
                      weight="medium"
                      variant="body"
                      color="surface.text.gray.muted">
                      Additional Details
                    </Text>
                    <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                      {loan.remarks ?? '—'}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box flex={1} paddingLeft="spacing.5">
                    <Text
                      size="small"
                      weight="medium"
                      variant="body"
                      color="surface.text.gray.muted">
                      Supporting Documents
                    </Text>
                    {loan.document_file_id ? (
                      <Link
                        size="small"
                        icon={DownloadIcon}
                        iconPosition="left"
                        onClick={() => downloadDocumentMutation.mutate(loan.document_file_id!)}
                        isDisabled={downloadDocumentMutation.isLoading}>
                        {downloadDocumentMutation.isLoading ? 'Loading…' : '[PDF] View Document'}
                      </Link>
                    ) : (
                      <Text size="small" weight="semibold">
                        —
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box
                borderWidth="thin"
                borderRadius="medium"
                borderColor="surface.border.gray.subtle"
                padding="spacing.7"
                display="flex"
                flexDirection="column"
                gap="spacing.6"
                backgroundColor="surface.background.gray.moderate">
                <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
                  Adjust Loan Terms
                </Heading>
                <Box display="flex" gap="spacing.7">
                  <Box width="263px" display="flex" flexDirection="column" gap="spacing.2">
                    <Text size="medium" weight="semibold" color="surface.text.gray.normal">
                      Amount and Interest
                    </Text>
                    <Text size="small" weight="regular" color="surface.text.gray.muted">
                      Approve the amount requested or modify it. Select an interest plan if
                      applicable
                    </Text>
                  </Box>
                  <Box display="flex" flexDirection="column" gap="spacing.7" flex={1}>
                    <TextInput
                      label="Amount"
                      prefix="₹"
                      value={form.amount}
                      onChange={({ value }) =>
                        setForm((prev) => ({ ...prev, amount: value ?? '' }))
                      }
                      type="number"
                      size="medium"
                    />
                    <Dropdown selectionType="single">
                      <SelectInput
                        label="Interest Type"
                        value={form.interestType}
                        onChange={({ values }) =>
                          setForm((prev) => ({ ...prev, interestType: values[0] ?? '0' }))
                        }
                      />
                      <DropdownOverlay>
                        <ActionList>
                          {INTEREST_TYPES.map((t) => (
                            <ActionListItem
                              key={t.value}
                              title={t.label}
                              description={t.description}
                              value={t.value}
                            />
                          ))}
                        </ActionList>
                      </DropdownOverlay>
                    </Dropdown>
                    {!isInterestFree && (
                      <>
                        <TextInput
                          label="Interest Rate (Per Annum)"
                          suffix="%"
                          value={form.interestRate}
                          onChange={({ value }) =>
                            setForm((prev) => ({ ...prev, interestRate: value ?? '' }))
                          }
                          type="number"
                          necessityIndicator="required"
                        />
                        <TextInput
                          label="Perquisite Rate"
                          suffix="%"
                          value={form.perquisiteRate}
                          onChange={({ value }) =>
                            setForm((prev) => ({ ...prev, perquisiteRate: value ?? '' }))
                          }
                          type="number"
                          helpText="The perquisite rate is the SBI interest rate minus the loan interest rate offered by the organisation."
                        />
                      </>
                    )}
                  </Box>
                </Box>
                <Box display="flex" gap="spacing.7">
                  <Box width="263px">
                    <Text size="medium" weight="semibold" color="surface.text.gray.normal">
                      Repayment Plan
                    </Text>
                    <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                      Confirm the duration of the loan and choose when the monthly deductions should
                      begin.
                    </Text>
                  </Box>
                  <Box flex={1} display="flex" flexDirection="column" gap="spacing.4">
                    <TextInput
                      label="Repayment Tenure"
                      suffix="Month(s)"
                      value={form.tenure}
                      onChange={({ value }) =>
                        setForm((prev) => ({ ...prev, tenure: value ?? '' }))
                      }
                      type="number"
                    />
                    <Dropdown selectionType="single">
                      <SelectInput
                        label="EMI Starts From"
                        value={form.emiStartsFrom || undefined}
                        onChange={({ values }) =>
                          setForm((prev) => ({ ...prev, emiStartsFrom: values[0] ?? '' }))
                        }
                        helpText="Select when EMI deductions should start"
                      />
                      <DropdownOverlay>
                        <ActionList>
                          {startDateOptions.map((opt) => (
                            <ActionListItem key={opt.value} title={opt.label} value={opt.value} />
                          ))}
                        </ActionList>
                      </DropdownOverlay>
                    </Dropdown>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="secondary"
                    size="medium"
                    isLoading={isCalculating}
                    isDisabled={!isCalculateEnabled}
                    onClick={() => calculateLoan()}>
                    Calculate Salary Impact
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box
              width="360px"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              gap="spacing.5">
              <Box backgroundColor="surface.background.gray.moderate" borderRadius="medium">
                <Accordion defaultExpandedIndex={0}>
                  <AccordionItem>
                    <AccordionItemHeader
                      title="Eligibility Checks"
                      trailing={
                        <Text size="medium" weight="semibold">
                          ({eligibilityPassedCount}/{eligibilityChecks.length})
                        </Text>
                      }
                    />
                    <AccordionItemBody>
                      <Box display="flex" flexDirection="column" gap="spacing.4">
                        {eligibilityChecks.map((check) => (
                          <Box key={check.label} display="flex" alignItems="center" gap="spacing.3">
                            {check.passed ? (
                              <CheckCircleIcon
                                color="feedback.icon.positive.intense"
                                size="medium"
                              />
                            ) : (
                              <XCircleIcon color="feedback.icon.negative.intense" size="medium" />
                            )}
                            <Text size="small" color="surface.text.gray.subtle">
                              {check.label}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    </AccordionItemBody>
                  </AccordionItem>
                </Accordion>
              </Box>

              <Box backgroundColor="surface.background.gray.moderate" borderRadius="medium">
                <Accordion defaultExpandedIndex={0}>
                  <AccordionItem>
                    <AccordionItemHeader title="Salary Impact Analysis" />
                    <AccordionItemBody>
                      {salaryImpactRows.map(({ label, value }) => (
                        <Box
                          key={label}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap="spacing.3">
                          <Text size="small" color="surface.text.gray.muted">
                            {label}
                          </Text>
                          <Box flexShrink={0}>
                            {value != null ? (
                              <Amount value={value} size="small" />
                            ) : (
                              <Text size="small">—</Text>
                            )}
                          </Box>
                        </Box>
                      ))}
                      <Divider />
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap="spacing.3">
                        <Text size="small" color="surface.text.gray.muted">
                          Est. New Take Home
                        </Text>
                        <Box flexShrink={0}>
                          {estNewTakeHome != null ? (
                            <Amount value={estNewTakeHome} size="small" />
                          ) : (
                            <Text size="small">—</Text>
                          )}
                        </Box>
                      </Box>
                    </AccordionItemBody>
                  </AccordionItem>
                </Accordion>
              </Box>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Box display="flex" gap="spacing.3">
              <Button
                color="negative"
                variant="secondary"
                onClick={() => setIsRejectDialogOpen(true)}>
                Reject
              </Button>
              <Button
                isLoading={editAndApproveMutation.isLoading}
                isDisabled={isApproveRejectDisabled}
                onClick={() => editAndApproveMutation.mutate()}>
                Approve
              </Button>
            </Box>
          </Box>
        </ModalFooter>
      </Modal>

      <RejectApplicationModal
        isOpen={isRejectDialogOpen}
        onDismiss={() => setIsRejectDialogOpen(false)}
        loanId={loan?.id ?? null}
      />
    </>
  );
};
